/** Shared Doctor Console Manager visual tokens (matches Doctor Console palette). */
export const MANAGER_COLORS = {
  primary: "#125386",
  primaryDark: "#0E3F66",
  accent: "#21cdc0",
  accentLight: "#e6f7f6",
  border: "#c3e8e5",
  borderLight: "#e8f4fc",
  pageBg: "#f4f9fc",
  card: "#ffffff",
  textMuted: "#5a6b7a",
  codeBg: "#f8fbfd",
};

export const pageShellSx = {
  minHeight: "100%",
  bgcolor: MANAGER_COLORS.pageBg,
  py: { xs: 2, md: 3 },
  px: { xs: 2, md: 3 },
};

export const pageInnerSx = {
  maxWidth: 1120,
  mx: "auto",
};

export const headerPaperSx = {
  p: { xs: 2, md: 2.5 },
  mb: 2,
  borderRadius: 2,
  border: `1px solid ${MANAGER_COLORS.borderLight}`,
  bgcolor: MANAGER_COLORS.card,
  boxShadow: "0 1px 3px rgba(18, 83, 134, 0.06)",
  position: "relative",
  overflow: "hidden",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: 4,
    background: `linear-gradient(90deg, ${MANAGER_COLORS.primary} 0%, ${MANAGER_COLORS.accent} 100%)`,
  },
};

export const toolbarPaperSx = {
  p: { xs: 1.5, md: 2 },
  mb: 2,
  borderRadius: 2,
  border: `1px solid ${MANAGER_COLORS.borderLight}`,
  bgcolor: MANAGER_COLORS.card,
  boxShadow: "0 1px 2px rgba(18, 83, 134, 0.04)",
};

export const contentPaperSx = {
  borderRadius: 2,
  border: `1px solid ${MANAGER_COLORS.borderLight}`,
  bgcolor: MANAGER_COLORS.card,
  boxShadow: "0 1px 3px rgba(18, 83, 134, 0.05)",
  overflow: "hidden",
};

export const tabsSx = {
  px: { xs: 1, md: 2 },
  borderBottom: `1px solid ${MANAGER_COLORS.borderLight}`,
  minHeight: 48,
  "& .MuiTab-root": {
    textTransform: "none",
    fontWeight: 500,
    fontSize: "0.9rem",
    minHeight: 48,
    color: MANAGER_COLORS.textMuted,
    "&.Mui-selected": {
      color: MANAGER_COLORS.primary,
      fontWeight: 600,
    },
  },
  "& .MuiTabs-indicator": {
    height: 3,
    borderRadius: "3px 3px 0 0",
    bgcolor: MANAGER_COLORS.accent,
  },
};

export const tabPanelSx = {
  p: { xs: 2, md: 2.5 },
};

export const primaryButtonSx = {
  bgcolor: MANAGER_COLORS.primaryDark,
  textTransform: "none",
  fontWeight: 600,
  boxShadow: "none",
  "&:hover": {
    bgcolor: MANAGER_COLORS.primary,
    boxShadow: "0 2px 8px rgba(14, 63, 102, 0.25)",
  },
  "&.Mui-disabled": {
    bgcolor: "action.disabledBackground",
  },
};

export const outlinedButtonSx = {
  textTransform: "none",
  fontWeight: 500,
  borderColor: MANAGER_COLORS.border,
  color: MANAGER_COLORS.primary,
  "&:hover": {
    borderColor: MANAGER_COLORS.primary,
    bgcolor: MANAGER_COLORS.accentLight,
  },
};

export const accordionSx = {
  borderTop: `1px solid ${MANAGER_COLORS.borderLight}`,
  bgcolor: "#fafcfd",
  "&::before": { display: "none" },
  "& .MuiAccordionSummary-root": {
    minHeight: 44,
    px: 2,
    "&.Mui-expanded": { minHeight: 44 },
  },
  "& .MuiAccordionSummary-content": {
    my: 1,
    "&.Mui-expanded": { my: 1 },
  },
};

export const dragListContainerSx = {
  border: `1px solid ${MANAGER_COLORS.border}`,
  borderRadius: 1.5,
  overflow: "hidden",
  mb: 2,
  bgcolor: MANAGER_COLORS.card,
};

export const dragRowSx = {
  display: "flex",
  alignItems: "center",
  gap: 1.5,
  px: 1.5,
  py: 1.25,
  borderBottom: `1px solid ${MANAGER_COLORS.borderLight}`,
  backgroundColor: MANAGER_COLORS.card,
  cursor: "grab",
  transition: "background-color 0.15s ease",
  "&:last-child": { borderBottom: "none" },
  "&:hover": {
    bgcolor: MANAGER_COLORS.accentLight,
  },
  "&:active": { cursor: "grabbing" },
};

export const helperCaptionSx = {
  display: "block",
  mb: 1.5,
  color: MANAGER_COLORS.textMuted,
  fontSize: "0.75rem",
};

export const emptyStateSx = {
  py: 6,
  px: 3,
  textAlign: "center",
  color: MANAGER_COLORS.textMuted,
};

export const versionCardSx = (isActive) => ({
  p: 2,
  mb: 1.5,
  borderRadius: 1.5,
  border: `1px solid ${isActive ? MANAGER_COLORS.accent : MANAGER_COLORS.borderLight}`,
  bgcolor: isActive ? MANAGER_COLORS.accentLight : MANAGER_COLORS.card,
  borderLeft: `4px solid ${isActive ? MANAGER_COLORS.accent : MANAGER_COLORS.border}`,
  transition: "box-shadow 0.15s ease",
  "&:hover": {
    boxShadow: "0 2px 8px rgba(18, 83, 134, 0.08)",
  },
});

export const featureRowSx = {
  display: "flex",
  alignItems: "center",
  gap: 2,
  flexWrap: "wrap",
  px: 2,
  py: 1.5,
  borderBottom: `1px solid ${MANAGER_COLORS.borderLight}`,
  "&:last-child": { borderBottom: "none" },
  "&:hover": { bgcolor: "#fafcfd" },
};

export const fieldHeaderRowSx = {
  display: "flex",
  alignItems: "center",
  gap: 1.5,
  px: 1.5,
  py: 0.75,
  bgcolor: MANAGER_COLORS.borderLight,
  borderBottom: `1px solid ${MANAGER_COLORS.border}`,
  "& .MuiTypography-root": {
    fontSize: "0.7rem",
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    color: MANAGER_COLORS.textMuted,
  },
};

export const fhirChipSx = {
  borderColor: MANAGER_COLORS.border,
  color: MANAGER_COLORS.primary,
  fontSize: "0.7rem",
  height: 24,
};

export const typeChipSx = {
  borderColor: MANAGER_COLORS.borderLight,
  bgcolor: "#fafcfd",
  fontSize: "0.7rem",
  height: 24,
};
